import { EMOJI_CATEGORIES } from "./emoji-data";

/**
 * Keyword search over the picker palette.
 *
 * Metadata (Polish + English labels and tags) comes from `emojibase-data` and is
 * loaded lazily the first time somebody types into the search box — the two
 * locale files weigh ~1 MB raw, far too much to ship in the main bundle for a
 * feature most users never touch.
 */

interface CompactEmoji {
  unicode: string;
  label: string;
  tags?: string[];
  skins?: CompactEmoji[];
}

/** Emoji character → searchable, diacritics-folded haystack. */
type EmojiIndex = Map<string, string>;

/** Strip variation selectors so `☺️` (emojibase) matches `☺` (our palette). */
function normalizeEmoji(value: string): string {
  return value.replace(/️/g, "");
}

/** Lowercase + remove diacritics so `zolw` finds `żółw` and vice versa. */
export function foldText(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/ł/g, "l");
}

function buildIndex(locales: CompactEmoji[][]): EmojiIndex {
  const index: EmojiIndex = new Map();
  const add = (entry: CompactEmoji) => {
    const key = normalizeEmoji(entry.unicode);
    const words = [entry.label, ...(entry.tags ?? [])].join(" ");
    const previous = index.get(key);
    index.set(key, previous ? `${previous} ${foldText(words)}` : foldText(words));
    entry.skins?.forEach(add);
  };
  locales.forEach((locale) => locale.forEach(add));
  return index;
}

let indexPromise: Promise<EmojiIndex> | null = null;

export function loadEmojiIndex(): Promise<EmojiIndex> {
  if (!indexPromise) {
    indexPromise = Promise.all([
      import("emojibase-data/pl/compact.json"),
      import("emojibase-data/en/compact.json"),
    ])
      .then(([pl, en]) => buildIndex([pl.default, en.default]))
      .catch((err) => {
        // Let the next keystroke retry instead of caching the failure forever.
        indexPromise = null;
        throw err;
      });
  }
  return indexPromise;
}

const ALL_PALETTE_EMOJI: string[] = EMOJI_CATEGORIES.flatMap((c) => c.emojis);

/**
 * Every palette emoji whose metadata contains ALL of the query words (in any
 * order), in palette order. Typing an emoji character itself also matches.
 */
export function searchEmoji(query: string, index: EmojiIndex): string[] {
  const words = foldText(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  const rawQuery = normalizeEmoji(query.trim());
  return ALL_PALETTE_EMOJI.filter((emoji) => {
    if (rawQuery && emoji === rawQuery) return true;
    const haystack = index.get(normalizeEmoji(emoji));
    if (!haystack) return false;
    return words.every((w) => haystack.includes(w));
  });
}
